import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TierartEnum, ValueLabelElement } from '@ps20-frontend/frontend-models';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { isListedBreed } from './breeds';
import { defaultUmfang } from './tierPositions';
import {
  TgvDogBreedType,
  TgvDogMotherBreedType,
  TgvKatalog,
  TgvTier,
  TgvTierForm,
} from './tiergruppe.model';

dayjs.extend(customParseFormat);

export const MAX_TIERE = 1000;
export const MIN_BIRTH_DATE = '1900-01-01';

const emptyForm = (): TgvTierForm => ({
  name: '',
  gender: null,
  birthDate: '',
  dogBreedType: null,
  breed: null,
  dogMotherBreedType: null,
  dogMotherBreed: null,
  dogSize: null,
  catKeepingType: null,
  hasIllnesses: null,
  selectedIllnesses: [],
});

export const createTier = (cardId: string): TgvTier => ({
  cardId,
  tierart: null,
  umfang: defaultUmfang(null),
  vorvertrag: null,
  ...emptyForm(),
});

/** Everything that depends on the animal type is reset when the type changes. */
export const withTierart = (tier: TgvTier, tierart: TierartEnum): TgvTier =>
  tier.tierart === tierart ? tier : resetForTierart(tier, tierart);

const resetForTierart = (tier: TgvTier, tierart: TierartEnum): TgvTier => ({
  ...tier,
  tierart,
  umfang: defaultUmfang(tierart),
  dogBreedType: tierart === TierartEnum.HUND ? TgvDogBreedType.SELECTED : null,
  breed: null,
  dogMotherBreedType: null,
  dogMotherBreed: null,
  dogSize: null,
  catKeepingType: null,
  hasIllnesses: null,
  selectedIllnesses: [],
});

/** Applies answers of the form and resets the answers that depended on a changed answer. */
export const withFormPatch = (tier: TgvTier, patch: Partial<TgvTierForm>): TgvTier => {
  let result: TgvTier = { ...tier, ...patch };
  const changed = (field: keyof TgvTierForm): boolean =>
    field in patch && patch[field] !== tier[field];
  if (changed('dogBreedType')) {
    result = {
      ...result,
      breed: null,
      dogMotherBreed: null,
      dogSize: null,
      dogMotherBreedType:
        patch.dogBreedType === TgvDogBreedType.MIXED ? TgvDogMotherBreedType.SELECTED : null,
    };
  }
  if (changed('dogMotherBreedType')) {
    result = { ...result, dogMotherBreed: null, dogSize: null };
  }
  if (changed('hasIllnesses')) {
    result = { ...result, selectedIllnesses: [] };
  }
  return result;
};

const isValidBirthDate = (birthDate: string): boolean => {
  const date = dayjs(birthDate, DATEFORMAT_LOCAL_STATE, true);
  return date.isValid() && !date.isAfter(dayjs(), 'day') && !date.isBefore(MIN_BIRTH_DATE, 'day');
};

export const breedTypeNeedsDogSize = (animal: TgvTier): boolean =>
  animal.tierart === TierartEnum.HUND &&
  (animal.dogBreedType === TgvDogBreedType.UNKNOWN ||
    (animal.dogBreedType === TgvDogBreedType.MIXED &&
      animal.dogMotherBreedType === TgvDogMotherBreedType.MIXED_OR_UNKNOWN));

const chosenBreedCode = (animal: TgvTier): string | null =>
  animal.dogBreedType === TgvDogBreedType.SELECTED && animal.breed !== null
    ? animal.breed.value
    : null;

export const thhListedBreedCode = (
  animal: TgvTier,
  thhDogBreeds: ValueLabelElement[],
): string | null => {
  const code = chosenBreedCode(animal);
  return code !== null && isListedBreed(code, thhDogBreeds) ? code : null;
};

const needsThhDogSize = (animal: TgvTier, thhDogBreeds: ValueLabelElement[]): boolean =>
  thhDogBreeds.length > 0 &&
  animal.tierart === TierartEnum.HUND &&
  animal.umfang.thh &&
  (animal.dogBreedType === TgvDogBreedType.MIXED ||
    (chosenBreedCode(animal) !== null && thhListedBreedCode(animal, thhDogBreeds) === null));

export const needsDogSize = (tier: TgvTier, thhDogBreeds: ValueLabelElement[]): boolean =>
  breedTypeNeedsDogSize(tier) || needsThhDogSize(tier, thhDogBreeds);

export const needsBreedSelect = (tier: TgvTier): boolean =>
  tier.tierart !== null &&
  (tier.tierart !== TierartEnum.HUND || tier.dogBreedType === TgvDogBreedType.SELECTED);

export type TierFieldError =
  | 'tierart'
  | 'umfang'
  | 'name'
  | 'namePattern'
  | 'gender'
  | 'birthDate'
  | 'dogBreedType'
  | 'breed'
  | 'dogMotherBreedType'
  | 'dogMotherBreed'
  | 'dogSize'
  | 'catKeepingType'
  | 'hasIllnesses'
  | 'selectedIllnesses'
  | 'denyingIllness';

/** The fields of a card that are still missing or invalid. Empty when the card is complete. */
/** The name is checked like in the TKV app: the whole name has to match the TIERNAME regexp. */
export const tiernamePattern = (regexps?: Record<string, string>): RegExp | null => {
  const source = regexps?.['TIERNAME'];
  return source ? new RegExp(`^(?:${source})$`) : null;
};

export interface AnimalValidationContext {
  namePattern: RegExp | null;
  thhDogBreeds: ValueLabelElement[];
}

export const animalValidationContext = (transientState: {
  regexps?: Record<string, string>;
  katalog: TgvKatalog;
}): AnimalValidationContext => ({
  namePattern: tiernamePattern(transientState.regexps),
  thhDogBreeds: transientState.katalog.thhDogBreeds,
});

export const tierErrors = (tier: TgvTier, context: AnimalValidationContext): TierFieldError[] => {
  if (tier.tierart === null) {
    return ['tierart'];
  }
  const errors: TierFieldError[] = [];
  const missing = (field: TierFieldError, isMissing: boolean): void => {
    if (isMissing) {
      errors.push(field);
    }
  };
  missing('umfang', !tier.umfang.tkv && !tier.umfang.thh);
  missing('name', tier.name.trim() === '');
  missing(
    'namePattern',
    tier.name.trim() !== '' && !!context.namePattern && !context.namePattern.test(tier.name.trim()),
  );
  missing('gender', tier.gender === null);
  missing('birthDate', !isValidBirthDate(tier.birthDate));
  if (tier.tierart === TierartEnum.HUND) {
    missing('dogBreedType', tier.dogBreedType === null);
    missing(
      'dogMotherBreedType',
      tier.dogBreedType === TgvDogBreedType.MIXED && tier.dogMotherBreedType === null,
    );
    missing(
      'dogMotherBreed',
      tier.dogMotherBreedType === TgvDogMotherBreedType.SELECTED && tier.dogMotherBreed === null,
    );
    missing('dogSize', needsDogSize(tier, context.thhDogBreeds) && tier.dogSize === null);
  }
  missing('breed', needsBreedSelect(tier) && tier.breed === null);
  missing('catKeepingType', tier.tierart === TierartEnum.KATZE && tier.catKeepingType === null);
  // The health questions belong to the TKV; a pure THH animal does not get them.
  missing('hasIllnesses', tier.umfang.tkv && tier.hasIllnesses === null);
  missing(
    'selectedIllnesses',
    tier.umfang.tkv && tier.hasIllnesses === true && tier.selectedIllnesses.length === 0,
  );
  missing(
    'denyingIllness',
    tier.umfang.tkv && tier.selectedIllnesses.some(illness => illness.deniesInsurance),
  );
  return errors;
};
